/**
 * useAgentGroups Hook
 * Fetches agent groups with real-time WebSocket updates
 */

import { useState, useEffect, useCallback } from 'react';
import { getAllAgentGroups, type AgentGroup } from '../services/agentGroupService';
import type { Socket } from 'socket.io-client';

interface UseAgentGroupsResult {
  groups: AgentGroup[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useAgentGroups(socket?: Socket | null): UseAgentGroupsResult {
  const [groups, setGroups] = useState<AgentGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchGroups = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getAllAgentGroups();
      setGroups(data);
    } catch (err) {
      console.error('Failed to fetch agent groups:', err);
      setError('Failed to load agent groups');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGroups();

    // Poll every 60 seconds
    const interval = setInterval(fetchGroups, 60000);

    return () => clearInterval(interval);
  }, [fetchGroups]);

  // WebSocket event handlers
  useEffect(() => {
    if (!socket) return;

    const handleGroupCreated = (newGroup: AgentGroup) => {
      setGroups((prev) => [...prev, newGroup].sort((a, b) => a.order - b.order));
    };

    const handleGroupUpdated = (updatedGroup: AgentGroup) => {
      setGroups((prev) =>
        prev
          .map((g) => (g.id === updatedGroup.id ? updatedGroup : g))
          .sort((a, b) => a.order - b.order)
      );
    };

    const handleGroupDeleted = ({ id }: { id: string }) => {
      setGroups((prev) => prev.filter((g) => g.id !== id));
    };

    const handleAgentsUpdated = (updatedGroup: AgentGroup) => {
      setGroups((prev) =>
        prev.map((g) => (g.id === updatedGroup.id ? updatedGroup : g))
      );
    };

    socket.on('agent-group:created', handleGroupCreated);
    socket.on('agent-group:updated', handleGroupUpdated);
    socket.on('agent-group:deleted', handleGroupDeleted);
    socket.on('agent-group:agents-updated', handleAgentsUpdated);

    return () => {
      socket.off('agent-group:created', handleGroupCreated);
      socket.off('agent-group:updated', handleGroupUpdated);
      socket.off('agent-group:deleted', handleGroupDeleted);
      socket.off('agent-group:agents-updated', handleAgentsUpdated);
    };
  }, [socket]);

  return {
    groups,
    isLoading,
    error,
    refresh: fetchGroups,
  };
}
